import React from 'react';
import { Link } from 'react-router-dom';
import { platformCoreNetworkApi } from '../../../api/platform/coreNetwork';
import { useAsyncData } from '../hooks/useAsyncData';
import {
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  MvpCard,
  MvpMetricCard,
  MvpTalentLayout,
  RouteLinkCard,
  StatusBadge,
} from '../components/MvpTalentUi';
import { formatDateTime } from '../utils/format';

const DashboardPage: React.FC = () => {
  const { data, loading, error, reload } = useAsyncData(async (signal) => {
    const [overview, pendingMembers, overdueWork, failedNotifications, securityEvents] = await Promise.all([
      platformCoreNetworkApi.getOverview({ signal }),
      platformCoreNetworkApi.listMembers({ status: 'pending', limit: 5, page: 1 }, { signal }),
      platformCoreNetworkApi.listWorkRequests({ overdue: true, limit: 5, page: 1 }, { signal }),
      platformCoreNetworkApi.listNotificationDeliveries({ failed: true, limit: 5, page: 1 }, { signal }),
      platformCoreNetworkApi.listSecurityEvents({ limit: 5, page: 1 }, { signal }),
    ]);

    return {
      overview,
      pendingMembers,
      overdueWork,
      failedNotifications,
      securityEvents,
    };
  }, []);

  return (
    <MvpTalentLayout
      title="Core Network"
      subtitle="Cross-instance oversight for provider operations, governance, support, and emergency controls."
      actions={<button type="button" className="mvp-button mvp-button--ghost" onClick={() => reload()}>Refresh</button>}
    >
      {loading ? <LoadingState label="Loading platform metrics..." /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <div className="mvp-metric-grid">
            <MvpMetricCard label="Total talent" value={data.overview.totalTalent} />
            <MvpMetricCard label="Pending approvals" value={data.overview.pendingTalent} tone="warning" />
            <MvpMetricCard label="Active talent" value={data.overview.activeTalent} />
            <MvpMetricCard label="Suspended talent" value={data.overview.suspendedTalent} tone="danger" />
            <MvpMetricCard label="Enabled instances" value={data.overview.enabledInstances} />
            <MvpMetricCard label="Active work" value={data.overview.activeWorkRequests} />
            <MvpMetricCard label="Overdue work" value={data.overview.overdueWorkRequests} tone="warning" />
            <MvpMetricCard label="Failed notifications" value={data.overview.failedNotifications} tone="danger" />
            <MvpMetricCard label="Security alerts" value={data.overview.openSecurityIssues} tone="danger" />
            <MvpMetricCard label="Outstanding payouts" value={data.overview.outstandingPayoutRecords} />
          </div>

          <div className="mvp-route-grid">
            <RouteLinkCard to="/command-center/mvp-talent/talent" title="Talent directory" description="Search and review global talent records." />
            <RouteLinkCard to="/command-center/mvp-talent/instances" title="Instance management" description="Enable Core Network and manage memberships by instance." />
            <RouteLinkCard to="/command-center/mvp-talent/work" title="Work oversight" description="Track cross-instance work requests and support escalations." />
            <RouteLinkCard to="/command-center/mvp-talent/settings" title="Platform settings" description="Emergency controls and global Core Network behavior." />
          </div>

          <div className="mvp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            <MvpCard title="Talent awaiting review">
              {data.pendingMembers.rows.length ? (
                <DataTable columns={['Talent', 'Status', 'Created', '']}>
                  {data.pendingMembers.rows.map((member) => (
                    <tr key={member.id}>
                      <td>{member.displayName}</td>
                      <td><StatusBadge status={member.globalStatus} /></td>
                      <td>{formatDateTime(member.createdAt)}</td>
                      <td><Link to={`/command-center/mvp-talent/talent/${member.id}`}>Open</Link></td>
                    </tr>
                  ))}
                </DataTable>
              ) : <EmptyState title="Nothing waiting" message="No talent profiles are pending review right now." />}
            </MvpCard>

            <MvpCard title="Overdue assignments">
              {data.overdueWork.rows.length ? (
                <DataTable columns={['Request', 'Instance', 'Status', 'Due', '']}>
                  {data.overdueWork.rows.map((request) => (
                    <tr key={request.id}>
                      <td>{request.title || `Request #${request.id}`}</td>
                      <td>{request.instanceName || request.instanceId}</td>
                      <td><StatusBadge status={request.status} /></td>
                      <td>{formatDateTime(request.deadlineAt)}</td>
                      <td><Link to={`/command-center/mvp-talent/work/${request.id}`}>Open</Link></td>
                    </tr>
                  ))}
                </DataTable>
              ) : <EmptyState title="No overdue work" message="No active Core Network work is currently overdue." />}
            </MvpCard>

            <MvpCard title="Failed notifications">
              {data.failedNotifications.rows.length ? (
                <DataTable columns={['Delivery', 'Type', 'Recipient', 'Status', '']}>
                  {data.failedNotifications.rows.map((delivery) => (
                    <tr key={delivery.id}>
                      <td>#{delivery.id}</td>
                      <td>{delivery.notificationType}</td>
                      <td>{delivery.recipientEmail || delivery.recipientMemberId || '-'}</td>
                      <td><StatusBadge status={delivery.status} /></td>
                      <td><Link to="/command-center/mvp-talent/notifications">Review</Link></td>
                    </tr>
                  ))}
                </DataTable>
              ) : <EmptyState title="Delivery queue healthy" message="No failed notification deliveries were returned by the platform API." />}
            </MvpCard>

            <MvpCard title="Recent security events">
              {data.securityEvents.rows.length ? (
                <DataTable columns={['Timestamp', 'Action', 'Actor', '']}>
                  {data.securityEvents.rows.map((event) => (
                    <tr key={event.id}>
                      <td>{formatDateTime(event.createdAt)}</td>
                      <td>{event.action}</td>
                      <td>{event.actorUser?.email || event.actorUserId}</td>
                      <td><Link to="/command-center/mvp-talent/security">Open</Link></td>
                    </tr>
                  ))}
                </DataTable>
              ) : <EmptyState title="No active alerts" message="No recent security events were returned by the platform API." />}
            </MvpCard>
          </div>
        </>
      ) : null}
    </MvpTalentLayout>
  );
};

export default DashboardPage;
